import * as React from 'react';
import { Row, Col } from 'react-bootstrap';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { reduxForm, Field } from 'redux-form';

import type { DeliverableStatusViewModel } from 'ab-viewModels/deliverableStatusTable.viewModel';
import type { EmployeeOptionVM } from 'ab-viewModels/employee/option.viewModel';

import type { DeliverableAssignmentRequestModel } from 'ab-requestModels/deliverable.requestModel';

import * as FORMS from 'af-constants/reduxForms';

import { Button } from '@acceligentllc/storybook';

import Dropdown from 'af-fields/Dropdown';
import Textarea from 'af-fields/Textarea';

import DropdownEmployeeItem from 'af-components/DropdownEmployeeItem';

import * as DeliverableStatusActions from 'af-actions/deliverableStatus';
import * as EmployeeActions from 'af-actions/employee';

import type DeliverableAssignmentFM from './formModel';
import { validate } from './validation';
import StatusOption from '../Shared/StatusOption';

const NOTES_MAX_LENGTH = 750;

type FormProps = InjectedFormProps<DeliverableAssignmentFM, FormOwnProps>;

interface OwnProps {
	isDisabled?: boolean;
	initialValues?: Nullable<FormProps['initialValues']>;
	onSubmit: (form: DeliverableAssignmentFM) => Promise<void>;
	onBack: () => void;
}

type FormOwnProps = OwnProps & ConnectedProps<typeof connector>;
type Props = FormOwnProps & FormProps;

interface State {
	employeesForCompany: EmployeeOptionVM[];
	deliverableStatusesForCompany: DeliverableStatusViewModel[];
}

class DeliverableAssignmentForm extends React.Component<Props, State> {
	static defaultProps: Partial<Props> = {
		isDisabled: false,
	};

	static readonly EMPLOYEE_FILTER_BY: (keyof EmployeeOptionVM)[] = ['firstName', 'lastName'];

	state: State = {
		employeesForCompany: [],
		deliverableStatusesForCompany: [],
	};

	static renderStatusOption = (option: DeliverableStatusViewModel) => <StatusOption status={option} />;

	static renderEmployeeItem = (option: EmployeeOptionVM) => option ? <DropdownEmployeeItem option={option} /> : <div />;

	lazyLoadEmployees = async (isLazyLoaded: boolean) => {
		const { findAllAssignableForDeliverable } = this.props;
		if (!isLazyLoaded) {
			const employeesForCompany = await findAllAssignableForDeliverable();
			this.setState(() => ({ employeesForCompany }));
		}
	};

	lazyLoadStatuses = async (isLazyLoaded: boolean) => {
		const { findAllStatuses } = this.props;
		if (!isLazyLoaded) {
			const statuses = await findAllStatuses();
			this.setState(() => ({ deliverableStatusesForCompany: statuses }));
		}
	};

	render() {
		const { handleSubmit, invalid, submitting, onBack, onSubmit, isDisabled } = this.props;
		const { deliverableStatusesForCompany, employeesForCompany } = this.state;

		return (
			<div className="form-box">
				<Row className="row--padded-top">
					<Col sm={8}>
						<Field
							component={Dropdown}
							id="statusId"
							label="Status *"
							name="statusId"
							onLazyLoad={this.lazyLoadStatuses}
							options={deliverableStatusesForCompany}
							placeholder="Select Data Status"
							propName="status"
							renderMenuItem={DeliverableAssignmentForm.renderStatusOption}
							valueKey="id"
							withCaret={true}
						/>
					</Col>
					<Col sm={8}>
						<Field
							component={Dropdown}
							disabled={isDisabled}
							filterable={true}
							filterBy={DeliverableAssignmentForm.EMPLOYEE_FILTER_BY}
							id="employeeId"
							label="Field worker *"
							name="employeeId"
							onLazyLoad={this.lazyLoadEmployees}
							options={employeesForCompany}
							placeholder="Select Field Worker"
							propName="employee"
							renderMenuItem={DeliverableAssignmentForm.renderEmployeeItem}
							valueKey="id"
							withCaret={true}
						/>
					</Col>
				</Row>
				<Row>
					<Col sm={16}>
						<Field
							component={Textarea}
							disabled={isDisabled}
							label="Notes"
							maxCharacters={NOTES_MAX_LENGTH}
							name="notes"
							placeholder="Enter Notes"
							rows={10}
							showMaxCharactersLabel={true}
						/>
					</Col>
				</Row>
				<Row className="row--submit">
					<Button
						label="Back"
						onClick={onBack}
						style="secondary"
					/>
					<Button
						disabled={invalid || isDisabled}
						label="Submit"
						onClick={handleSubmit(onSubmit)}
						style="primary"
						submitting={submitting}
					/>
				</Row>
			</div>
		);
	}
}

function mapDispatchToProps() {
	return {
		findAllAssignableForDeliverable: EmployeeActions.findAllEmployeesForWorkOrders,
		findAllStatuses: DeliverableStatusActions.findAllDataStatusesForCompany,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	connector,
	reduxForm<DeliverableAssignmentRequestModel, FormOwnProps>({ form: FORMS.DELIVERABLE_ASSIGNMENT, validate, enableReinitialize: true })
);
export default enhance(DeliverableAssignmentForm);
